import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { practiceSettingsPath } from "../../../../shared/navigation/practiceSettings";
import { TimeSlot } from "../../domain/types";
import TimeSlotCombobox from "./TimeSlotCombobox";

interface SlotGridProps {
    slots: TimeSlot[];
    timezone: string;
    selected: string | null;
    onSelect: (start: string) => void;
    isLoading?: boolean;
    isError?: boolean;
    onRetry?: () => void;
    disabled?: boolean;
    /** Whether the therapist works this weekday at all. */
    dayHasAvailability?: boolean;
    /** Whether any weekly hours exist yet. */
    hasAnyAvailability?: boolean;
}

/**
 * The bookable times of a day, or why there are none.
 *
 * Times are always rendered in `timezone` (the therapist's), never the
 * viewer's, and the zone abbreviation is shown beside them.
 */
export default function SlotGrid({
    slots,
    timezone,
    selected,
    onSelect,
    isLoading = false,
    isError = false,
    onRetry,
    disabled = false,
    dayHasAvailability = true,
    hasAnyAvailability = true,
}: SlotGridProps) {
    const { t } = useTranslation();

    if (isLoading) {
        return (
            <p role="status" className="text-sm text-gray-500">
                {t("scheduler.schedule.loadingSlots")}
            </p>
        );
    }

    if (isError) {
        return (
            <div role="alert" className="flex items-center gap-2 text-sm">
                <span className="text-red-600">
                    {t("scheduler.schedule.slotsError")}
                </span>
                {onRetry && (
                    <button type="button" onClick={onRetry} className="underline">
                        {t("scheduler.common.retry")}
                    </button>
                )}
            </div>
        );
    }

    if (slots.length === 0) {
        // "You don't work this day" and "this day is full" look identical in a
        // blank list, so say which one it is — and point at the fix.
        if (!hasAnyAvailability) {
            return (
                <div className="flex flex-col items-start gap-1 text-sm">
                    <p className="text-gray-500">
                        {t("scheduler.schedule.noAvailabilityAtAll")}
                    </p>
                    <Link to={practiceSettingsPath("scheduler")} className="text-brand-600 underline">
                        {t("scheduler.schedule.setAvailability")}
                    </Link>
                </div>
            );
        }
        if (!dayHasAvailability) {
            return (
                <div className="flex flex-col items-start gap-1 text-sm">
                    <p className="text-gray-500">
                        {t("scheduler.schedule.noAvailabilityThisWeekday")}
                    </p>
                    <Link to={practiceSettingsPath("scheduler")} className="text-brand-600 underline">
                        {t("scheduler.schedule.setAvailability")}
                    </Link>
                </div>
            );
        }
        return (
            <p className="text-sm text-gray-500">{t("scheduler.schedule.noSlots")}</p>
        );
    }

    return (
        <TimeSlotCombobox
            slots={slots}
            timezone={timezone}
            selected={selected}
            onSelect={onSelect}
            disabled={disabled}
        />
    );
}
